import React, { useState, useEffect } from "react";
import { getSupabaseClient } from "../../../lib/supabase";
import { Guide } from "../../types/guide";
import { cardStyle, emptyTextStyle, mutedTextStyle } from "../GuideDetailModal.styles";
import { formatDateTime } from "../GuideDetailModal.utils";

export function BackgroundChecksTab({ guide }: { guide: Guide }) {
    const rows = guide.onboarding?.backgroundChecks ?? [];
    const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

    useEffect(() => {
        async function loadSignedUrls() {
            const supabase = getSupabaseClient();
            const urlMap: Record<string, string> = {};

            for (const row of rows) {
                const key = row.storageObjectKey || row.filePath;
                if (!key) continue;

                try {
                    const { data, error } = await supabase.storage
                        .from('background-checks')
                        .createSignedUrl(key, 3600);

                    if (data?.signedUrl && !error) {
                        urlMap[row.id ?? key] = data.signedUrl;
                    }
                } catch (err) {
                    console.error('Failed to get signed URL for:', key, err);
                }
            }

            setSignedUrls(urlMap);
        }

        if (rows.length > 0) {
            loadSignedUrls();
        }
    }, [rows]);

    if (!rows.length) {
        return <div style={emptyTextStyle}>No background checks have been uploaded.</div>;
    }

    const getImageUrl = (row: any) => {
        // Try signed URL first (works for private buckets)
        const signedUrl = signedUrls[row.id ?? row.storageObjectKey ?? row.filePath];
        if (signedUrl) return signedUrl;

        // Fallback to public URL if available
        if (row.publicUrl) return row.publicUrl;

        return null;
    };

    const isPdf = (row: any) => {
        const filename = (row.originalFilename ?? row.storageObjectKey ?? row.filePath ?? "").toLowerCase();
        return filename.endsWith(".pdf");
    };

    return (
        <div style={{ display: "grid", gap: 16, maxWidth: "100%", overflowX: "hidden" }}>
            {rows.map((row, index) => {
                const fileUrl = getImageUrl(row);
                const pdf = isPdf(row);

                return (
                    <div
                        key={row.id ?? row.filePath ?? row.submittedAt ?? row.createdAt ?? `background-${index}`}
                        style={cardStyle}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
                            <div style={{ fontWeight: 600 }}>Background Check</div>
                            <div style={mutedTextStyle}>{formatDateTime(row.submittedAt ?? row.createdAt)}</div>
                        </div>

                        {/* Document Display */}
                        {fileUrl ? (
                            <div style={{ marginBottom: 12 }}>
                                {pdf ? (
                                    <iframe
                                        src={fileUrl}
                                        title="Background Check Document"
                                        style={{
                                            width: "100%",
                                            height: "500px",
                                            border: "1px solid #2C2731",
                                            borderRadius: 16,
                                            backgroundColor: "#FEF6D6",
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={fileUrl}
                                        alt="Background Check Document"
                                        style={{
                                            width: "100%",
                                            maxWidth: "100%",
                                            maxHeight: "400px",
                                            objectFit: "contain",
                                            border: "1px solid #2C2731",
                                            borderRadius: 16,
                                            backgroundColor: "#FEF6D6",
                                            display: "block"
                                        }}
                                        onError={(e) => {
                                            console.error('Image failed to load:', fileUrl);
                                            const target = e.target as HTMLImageElement;
                                            target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIGZhaWxlZCB0byBsb2FkPC90ZXh0Pjwvc3ZnPg==";
                                            target.style.maxHeight = "100px";
                                        }}
                                    />
                                )}
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={row.originalFilename}
                                    style={{
                                        display: "inline-block",
                                        marginTop: 8,
                                        fontSize: 13,
                                        color: "#6B46C1",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                    }}
                                >
                                    Download {row.originalFilename ?? "file"}
                                </a>
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: 32,
                                    textAlign: "center",
                                    backgroundColor: "#FEF6D6",
                                    border: "1px dashed rgba(44,39,49,0.3)",
                                    borderRadius: 16,
                                    marginBottom: 12,
                                    color: "#403851",
                                }}
                            >
                                <div>No file URL available</div>
                            </div>
                        )}
                        
                        {/* File Details */}
                        <div style={{ 
                            color: "#2C2731", 
                            fontSize: 13, 
                            borderTop: "1px solid rgba(44,39,49,0.12)",
                            paddingTop: 8,
                            wordBreak: "break-all"
                        }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap" }}>
                                <span>Status:</span>
                                <span style={{ 
                                    fontWeight: 500,
                                    color: row.status === "approved" ? "#059669" : 
                                           row.status === "rejected" ? "#dc2626" : "#d97706"
                                }}>
                                    {row.status ?? "pending"}
                                </span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap" }}>
                                <span>Filename:</span>
                                <span style={{ maxWidth: "60%", wordBreak: "break-all" }}>{row.originalFilename ?? "Unknown"}</span>
                            </div>
                            {row.storageObjectKey && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ marginBottom: 4 }}>File ID:</div>
                                    <div style={{ 
                                        fontFamily: "monospace", 
                                        fontSize: 10,
                                        backgroundColor: "#F2EAC3",
                                        padding: 4,
                                        borderRadius: 8,
                                        wordBreak: "break-all"
                                    }}>
                                        {row.storageObjectKey}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
